import { InvalidTimeframe } from '../error';
export class SortedTimeframeCollection {
    constructor(timeframes = []) {
        this._timeframes = [...timeframes];
        this.sortAscending();
    }
    get timeframes() {
        return this._timeframes;
    }
    insert(timeframe, preserveAdjacentTimeframes = false) {
        var _a, _b;
        validate(timeframe);
        if (this.isEmpty()) {
            this.timeframes.push(timeframe);
            return;
        }
        if (this.timeframes.some(x => encloses(x, timeframe))) {
            return;
        }
        // adjust new timeframe to enclose overlapping timeframes
        const timeframeOverlappingStart = preserveAdjacentTimeframes
            ? this.timeframes.find(x => containsExclusiveEnd(x, timeframe.start))
            : this.timeframes.find(x => containsInclusive(x, timeframe.start));
        const timeframeOverlappingEnd = preserveAdjacentTimeframes
            ? this.timeframes.find(x => containsExclusiveStart(x, timeframe.end))
            : this.timeframes.find(x => containsInclusive(x, timeframe.end));
        const newTimeframe = {
            start: (_a = timeframeOverlappingStart === null || timeframeOverlappingStart === void 0 ? void 0 : timeframeOverlappingStart.start) !== null && _a !== void 0 ? _a : timeframe.start,
            end: (_b = timeframeOverlappingEnd === null || timeframeOverlappingEnd === void 0 ? void 0 : timeframeOverlappingEnd.end) !== null && _b !== void 0 ? _b : timeframe.end,
        };
        // remove timeframes enclosed by new timeframe
        this.removeTimeframes(this.timeframes.filter(x => encloses(newTimeframe, x)));
        this.timeframes.push(newTimeframe);
        this.sortAscending();
    }
    remove(timeframe) {
        validate(timeframe);
        if (this.isEmpty()) {
            return;
        }
        // remove timeframes enclosed by timeframe
        this.removeTimeframes(this.timeframes.filter(x => encloses(timeframe, x)));
        // trim overlapping timeframes
        const timeframeOverlappingStart = this.timeframes.find(x => containsExclusive(x, timeframe.start));
        const timeframeOverlappingEnd = this.timeframes.find(x => containsExclusive(x, timeframe.end));
        if (timeframeOverlappingStart) {
            this.removeTimeframes([timeframeOverlappingStart]);
            this.timeframes.push({ start: timeframeOverlappingStart.start, end: timeframe.start });
        }
        if (timeframeOverlappingEnd) {
            this.removeTimeframes([timeframeOverlappingEnd]);
            this.timeframes.push({ start: timeframe.end, end: timeframeOverlappingEnd.end });
        }
        this.sortAscending();
    }
    clear() {
        this._timeframes = [];
    }
    contains(secondOfDay) {
        if (secondOfDay < 0 || 86399 < secondOfDay) {
            throw new RangeError(`secondOfDay (${secondOfDay}) is outside the acceptable range 0 to 86399`);
        }
        return this.timeframes.some(x => containsExclusiveEnd(x, secondOfDay));
    }
    isEmpty() {
        return this.timeframes.length === 0;
    }
    removeTimeframes(timeframes) {
        this._timeframes = this.timeframes.filter(x => !includes(timeframes, x));
    }
    sortAscending() {
        this._timeframes.sort((a, b) => {
            if (a.start === b.start) {
                return 0;
            }
            return a.start < b.start ? -1 : 1;
        });
    }
}
function containsExclusive(timeframe, secondOfDay) {
    return timeframe.start < secondOfDay && secondOfDay < timeframe.end;
}
function containsExclusiveEnd(timeframe, secondOfDay) {
    return timeframe.start <= secondOfDay && secondOfDay < timeframe.end;
}
function containsExclusiveStart(timeframe, secondOfDay) {
    return timeframe.start < secondOfDay && secondOfDay <= timeframe.end;
}
function containsInclusive(timeframe, secondOfDay) {
    return timeframe.start <= secondOfDay && secondOfDay <= timeframe.end;
}
function encloses(timeframe, other) {
    return timeframe.start <= other.start && other.end <= timeframe.end;
}
function equals(timeframe, other) {
    return timeframe.start === other.start && timeframe.end === other.end;
}
function includes(timeframes, timeframe) {
    return timeframes.some(x => equals(x, timeframe));
}
function validate(timeframe) {
    if (timeframe.end <= timeframe.start) {
        throw new InvalidTimeframe(timeframe);
    }
}
